import React from "react";
import { styled } from "@mui/system";
import CrownIcon from "src/icons/CrownIcon";
import TrophyIcon from "src/icons/TrophyIcon";

export const GRADIENT_BRONZE_BORDER =
  "radial-gradient(85.74% 85.74% at 80.50% 50%, rgba(51, 31, 0, 0.65) 0%, rgba(238, 241, 245, 0.26) 100%)";
export const GRADIENT_SILVER_BORDER =
  "radial-gradient(85.74% 85.74% at 80.50% 50%, rgb(193, 193, 193) 0%, rgba(238, 241, 245, 0.26) 100%)";
export const GRADIENT_GOLD_BORDER =
  "radial-gradient(85.74% 85.74% at 80.50% 50%, rgba(212, 175, 55, 0.65) 0%, rgba(238, 241, 245, 0.26) 100%)";
export const GRADIENT_BLACK_BORDER =
  "radial-gradient(91.19% 91.19% at 89.50% 44%, rgb(0, 0, 0) 28.90%, rgba(229, 225, 215, 0.50) 83.50%)";

interface MedalWrapperProps {
  background: string;
  size: string;
  scale?: string;
}
const MedalWrapper = styled("div")<MedalWrapperProps>(({ background, scale, size }) => ({
  display: "flex",
  width: size,
  height: size,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "100px",
  background: background,
  transform: `scale(${scale})`,
}));

interface InnerCircleProps {
  size: string;
  defaultBackground: boolean;
}
const InnerCircle = styled("div")<InnerCircleProps>(({ size, defaultBackground }) => ({
  width: size,
  height: size,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "80px",
  background: defaultBackground ? "rgba(248, 249, 249, 0.70)" : "rgba(248, 249, 249, 0.60)",
}));

interface IconWrapperProps {
  scale: string;
}
const TrophyWrapper = styled("div")<IconWrapperProps>(({ scale }) => ({
  width: "64px",
  height: "64px",
  flexShrink: 0,
  transform: `scale(${scale})`,
}));

const CrownWrapper = styled("div")<IconWrapperProps>(({ scale }) => ({
  width: "47.25px",
  height: "42px",
  flexShrink: 0,
  transform: `scale(${scale})`,
}));

interface SparkleProps {
  left: string;
  bottom: string;
}

const Sparkle1 = styled("div")<SparkleProps>(({ left, bottom }) => ({
  width: "0px",
  height: "0px",
  transform: "rotate(13.285deg)",
  position: "relative",
  left: left,
  bottom: bottom,
  fill: "#FCF6EB",
  strokeWidth: "1px",
  stroke: "#002035",
  zIndex: 99,
}));

const Sparkle2 = styled("div")<SparkleProps>(({ left, bottom }) => ({
  width: "0px",
  height: "0px",
  transform: "rotate(13.285deg)",
  flexShrink: 0,
  position: "relative",
  left: left,
  bottom: bottom,
  fill: "#FCF6EB",
  strokeWidth: "1px",
  stroke: "#002035",
}));

const smallDimensions = {
  medalWrapper: { size: "64px" },
  innerCircle: { size: "51.2px" },
  trophyWrapper: { scale: ".7" },
  crownWrapper: { scale: ".8" },
  sparkle1: { left: "18px", bottom: "65px", size: "8" },
  sparkle2: { left: "25px", bottom: "75px", size: "7" },
};
const mediumDimensions = {
  medalWrapper: { size: "100px" },
  innerCircle: { size: "80px" },
  trophyWrapper: { scale: "1" },
  crownWrapper: { scale: "1" },
  sparkle1: { left: "25px", bottom: "68px", size: "14" },
  sparkle2: { left: "40px", bottom: "85px", size: "10" },
};

const getDimensions = (size?: "small" | "medium") => {
  if (size === "small") {
    return smallDimensions;
  }

  return mediumDimensions;
};

interface MedalProps {
  tier: "bronze" | "silver" | "gold" | "black";
  size?: "small" | "medium";
}

/**
 * A medal component to represent different tiers.
 * @param {"bronze" | "silver" | "gold" | "black"} props.tier - The tier of the medal (bronze, silver, gold, or black).
 * @param {"small" | "medium"} [props.size="medium"] - The size of the medal (default is medium).
 */
const Medal = (props: MedalProps) => {
  const { tier, size = "medium" } = props;

  const { medalWrapper, innerCircle, trophyWrapper, crownWrapper, sparkle1, sparkle2 } =
    getDimensions(size);

  let background = "";
  let isDefault = false;
  switch (tier) {
    case "bronze":
      background = GRADIENT_BRONZE_BORDER;
      break;
    case "silver":
      background = GRADIENT_SILVER_BORDER;
      break;
    case "gold":
      background = GRADIENT_GOLD_BORDER;
      break;
    case "black":
      background = GRADIENT_BLACK_BORDER;
      break;
    default:
      background = "#002035";
      isDefault = true;
      break;
  }

  return (
    <>
      <MedalWrapper id="RaF-medal" background={background} {...medalWrapper}>
        <InnerCircle {...innerCircle} defaultBackground={isDefault}>
          {tier === "black" ? (
            <CrownWrapper {...crownWrapper}>
              <CrownIcon height="42" width="48" />
            </CrownWrapper>
          ) : (
            <TrophyWrapper {...trophyWrapper}>
              <TrophyIcon color="#002035" size="64" />
            </TrophyWrapper>
          )}

          {/* Sparkle 2 - the smaller sparkle*/}
          <Sparkle2 {...sparkle2}>
            <Sparkle {...sparkle2} />
          </Sparkle2>

          {/* Sparkle 1 - the bigger sparkle*/}
          <Sparkle1 {...sparkle1}>
            <Sparkle {...sparkle1} />
          </Sparkle1>
        </InnerCircle>
      </MedalWrapper>
    </>
  );
};

export default Medal;

const Sparkle = ({ size }: { size: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 13 13"
      fill="none">
      <path
        d="M6.22007 2.39687L6.22012 2.39676L6.81539 1.18108C6.93721 0.932374 7.21393 0.802111 7.48187 0.865374C7.74978 0.928631 7.93901 1.16888 7.93674 1.44579C7.93674 1.44581 7.93674 1.44583 7.93674 1.44585L7.9254 2.79939L7.9254 2.79951C7.91357 4.2518 8.73513 5.58124 10.0393 6.22025L10.0394 6.2203L11.2551 6.81557C11.2551 6.81558 11.2551 6.81559 11.2552 6.8156C11.5038 6.93742 11.6341 7.21413 11.5708 7.48205C11.5076 7.74998 11.2673 7.93922 10.9903 7.93693L9.63681 7.92558L9.63669 7.92558C8.1844 7.91375 6.85496 8.73531 6.21595 10.0395L6.2159 10.0396L5.62063 11.2553C5.62062 11.2553 5.62061 11.2553 5.6206 11.2553C5.49878 11.504 5.22207 11.6343 4.95415 11.571C4.68622 11.5077 4.49698 11.2675 4.49927 10.9905L4.51062 9.63699L4.51062 9.63687C4.52245 8.18458 3.70089 6.85514 2.39668 6.21613L2.39658 6.21608L1.1809 5.62081C1.18088 5.6208 1.18087 5.62079 1.18085 5.62079C0.932175 5.49896 0.801932 5.22225 0.86519 4.95433C0.928452 4.6864 1.16873 4.49717 1.44567 4.49946L2.79921 4.51081L2.79933 4.51081C4.25162 4.52263 5.58106 3.70107 6.22007 2.39687Z"
        fill="#FCF6EB"
        stroke="#002035"
      />
    </svg>
  );
};
